/**
 * FundraiserDashboard.tsx - Main Dashboard and Fundraiser Management Interface
 *
 * This is the primary dashboard component that displays all fundraisers, statistics,
 * and provides management actions. It serves as the main hub for fundraiser operations.
 *
 * Key Features:
 * - Fundraiser overview with statistics and top performers
 * - Search and filtering capabilities
 * - Bulk actions and individual fundraiser management
 * - Modal integrations for sharing, embedding, and editing
 * - Empty state handling and onboarding
 * - Quick access to settings, gallery, and creation tools
 *
 * Actions Supported:
 * - Create, edit, delete, copy fundraisers
 * - Share links, social sharing, embed codes
 * - Promoter invitations and management
 * - Preview and donation testing
 *
 * Dependencies: MobX store, dashboard components, modal components, table components
 */

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import {
  Plus,
  Rocket,
  Settings,
  CreditCard,
  LayoutGrid,
  UserPlus,
  X,
  Filter,
  RotateCcw,
} from "lucide-react";
import { Eye } from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { Toast } from "./Toast";
import CopyFundraiserModal from "./CopyFundraiserModal";
import { DashboardStats } from "./dashboard/DashboardStats";
import { TopPerformingFundraisers } from "./dashboard/TopPerformingFundraisers";
import { SearchAndFilters } from "./dashboard/SearchAndFilters";
import { FundraisersTable } from "./dashboard/FundraisersTable";
import { SocialShareModal } from "./SocialShareModal";
import { EmbedFundraiserModal } from "./EmbedFundraiserModal";
import { UpdateLinkModal } from "./UpdateLinkModal";
import { DonationCapture } from "./donation-capture/DonationCapture";
import { DeleteFundraiserModal } from "./DeleteFundraiserModal";
import { AIFundraiserChat } from "./ai-fundraiser-create/AIFundraiserChat";
import { firstValueFrom } from "rxjs";
import { FundraiserService } from "../services";
import {
  CreateFundraiserModel,
  FundRaiserDashboardModel,
  FundRaiserGridModel,
  GallerySettingsModel,
} from "../models";
import {
  AuthService,
  Loader,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { Fundraiser } from "../models/FundraisersGridModels";
import { FundRaiserStatus } from "../enums";
import { NavigateFunction } from "react-router-dom";
import FundraiserEmptyState from "./FundraiserEmptyState";
import { ApplicationSettingsStore } from "@mindmemobile/mm-payment-modules";
import ToastService from "./ToastService";
import { canManageFundraiser } from "../utils/permissions";
const DashboardContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    #f8fafc 0%,
    #e2e8f0 25%,
    #f1f5f9 50%,
    #ffffff 100%
  );
  width: 100%;
`;

const DashboardContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 47px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
`;

const CreateButton = styled.button`
  background: linear-gradient(135deg, #10b981, #059669);
  color: #ffffff;
  border: none;
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.9;
    transform: none;
    box-shadow: none;
  }
`;

const GalleryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  backdrop-filter: blur(12px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.9);
  color: #64748b;
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 1);
    color: #1e293b;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 2px solid #e2e8f0;
  background: none;
  color: #3b82f6;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #3b82f6;
    transform: translateY(-1px);
  }
`;

const IconsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Section = styled.div`
  margin-bottom: 40px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 20px 14px 48px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 16px center;
  background-size: 20px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: #1e293b;
    background: #f1f5f9;
  }
`;

const FilterControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilterButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: 2px solid ${(props) => (props.active ? "#10b981" : "#e2e8f0")};
  background: ${(props) => (props.active ? "#f0fdf4" : "#ffffff")};
  color: ${(props) => (props.active ? "#10b981" : "#64748b")};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: #10b981;
    background: #f8fafc;
  }
`;

const FilterBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #10b981;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 50%;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
`;

const ResetButton = styled.button<{ visible: boolean }>`
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  background: #ffffff;
  color: #64748b;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

type IFundraiserDashboardProps = {
  navigate: NavigateFunction;
  location: Location;
};

const FundraiserDashboard = observer(
  ({ navigate, location }: IFundraiserDashboardProps) => {
    const { fundraisers } = fundraiserStore;
    const [showCopyModal, setShowCopyModal] = React.useState(false);
    const [selectedCopyCampaign, setSelectedCopyCampaign] =
      React.useState<CreateFundraiserModel | null>(null);
    const [showLinkModal, setShowLinkModal] = React.useState(false);
    const [showSocialShareModal, setShowSocialShareModal] =
      React.useState(false);
    const [showEmbedModal, setShowEmbedModal] = React.useState(false);
    const [showFilters, setShowFilters] = React.useState(false);
    const [showEmptyStatePlaceholder, setShowEmptyStatePlaceholder] =
      React.useState(false);
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [fundraiserToDelete, setFundraiserToDelete] =
      React.useState<CreateFundraiserModel | null>(null);
    const [showAIChat, setShowAIChat] = React.useState(false);
    const [selectedModalFundraiser, setSelectedModalFundraiser] =
      React.useState<any>(null);
    const [searchTerm, setSearchTerm] = React.useState("");
    //const [showDonationCapture, setShowDonationCapture] = React.useState(false);
    const [urlError, setUrlError] = React.useState(false);
    const [isLoadingDashboard, setIsLoadingDashboard] = useState(true);
    const [firstTimeLoad, setFirstTimeLoad] = useState(true);

    const [filters, setFilters] = React.useState({
      status: [] as FundRaiserStatus[],
      amountMin: "",
      amountMax: "",
      dateMin: "",
      dateMax: "",
    });

    const [dashboardStats, setDashboardStats] =
      useState<FundRaiserDashboardModel>(new FundRaiserDashboardModel());

    const hasActiveFilters =
      filters.status.length > 0 ||
      filters.amountMin ||
      filters.amountMax ||
      filters.dateMin ||
      filters.dateMax
        ? true
        : false;

    useEffect(() => {
      fundraiserStore.toggleFundRaiserGridLoading(true);
      fetchFundraisers(true);
      loadStats();
      fundraiserStore.fetchMerchantAccounts("");
    }, [location.search]);

    const onAICreate = () => {
      fetchFundraisers(true, false, false);
      loadStats(false);
    };

    useEffect(() => {
      if (firstTimeLoad) return;
      const debounceTimeout = setTimeout(() => {
        fetchFundraisers(false, true);
      }, 500); // 500ms debounce delay

      return () => clearTimeout(debounceTimeout);
    }, [filters, searchTerm]);

    function loadStats(showLoader = true) {
      if (!AuthService.authData.accountId) {
        setIsLoadingDashboard(false);
        return;
      }
      setIsLoadingDashboard(showLoader);
      FundraiserService.getDashboradData(
        AuthService.authData.accountId
      ).subscribe({
        next: (response) => {
          if (response.result) {
            const dashboardModel = FundRaiserDashboardModel.deserializeAPIModel(
              response.result
            );
            setIsLoadingDashboard(false);
            setDashboardStats(dashboardModel);
          }
        },
      });
    }

    const fetchFundraisers = async (
      isFirstTimeLoad = false,
      isFromFilter = false,
      showLoader = true
    ) => {
      setFirstTimeLoad(false);
      if (!isFromFilter && showLoader) {
        fundraiserStore.toggleFundRaiserGridLoading(true);
      }
      try {
        if (!AuthService.authData.accountId) {
          fundraiserStore.setFundraisers([]);
          fundraiserStore.toggleFundRaiserGridLoading(false);
          setShowEmptyStatePlaceholder(true);
          return;
        }
        const response = await firstValueFrom(
          FundraiserService.getFundraisers(
            AuthService.authData.accountId,
            1,
            10000,
            searchTerm,
            filters.amountMin ? parseFloat(filters.amountMin) : undefined,
            filters.amountMax ? parseFloat(filters.amountMax) : undefined,
            filters.dateMin ? new Date(filters.dateMin) : undefined,
            filters.dateMax ? new Date(filters.dateMax) : undefined,
            undefined,
            filters.status
          )
        );
        const data = response.result.map((item) =>
          CreateFundraiserModel.deserializeGridAPIModel(item)
        );
        fundraiserStore.setFundraisers(data);
        fundraiserStore.toggleFundRaiserGridLoading(false);
        if (isFirstTimeLoad && data.length === 0) {
          setShowEmptyStatePlaceholder(true);
        } else {
          if (showEmptyStatePlaceholder) {
            setShowEmptyStatePlaceholder(false);
          }
        }
      } catch (error) {
        console.error("Failed to load fundraisers", error);
        fundraiserStore.toggleFundRaiserGridLoading(false);
        if (isFirstTimeLoad) {
          setShowEmptyStatePlaceholder(true);
        }
      }
    };

    const copyFundraiserLink = async (fundraiserId: string) => {
      const baseUrl = fundraiserStore.getFundRaiserDomainBaseURL();
      const url = `${baseUrl}/fundraiser/${fundraiserId}`;

      try {
        await navigator.clipboard.writeText(url);
        ToastService.showSuccess("Fundraiser link copied to clipboard");
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        ToastService.showSuccess("Fundraiser link copied to clipboard");
      }
    };

    const handleCopyClick = (
      e: React.MouseEvent,
      fundraiser: CreateFundraiserModel
    ) => {
      setSelectedCopyCampaign(fundraiser);
      setShowCopyModal(true);
    };

    const handleLinkClick = (fundraiserId: string) => {
      const fundraiser = fundraisers.find((f) => f.id === fundraiserId);
      if (fundraiser) {
        setSelectedModalFundraiser(fundraiser);
        // fundraiserStore.setSelectedFundraiserForModal(fundraiserId);
      }
      setShowLinkModal(true);
    };

    const handleShareClick = (fundraiserId: string) => {
      const fundraiser = fundraisers.find((f) => f.id === fundraiserId);
      if (fundraiser) {
        setSelectedModalFundraiser(fundraiser);
        // fundraiserStore.setSelectedFundraiserForModal(fundraiserId);
      }
      setShowSocialShareModal(true);
    };

    const handleEmbedClick = (fundraiser: any) => {
      setSelectedModalFundraiser(fundraiser);
      // fundraiserStore.setSelectedFundraiserForModal(fundraiser.id);
      setShowEmbedModal(true);
    };

    const handlePreviewClick = (fundraiserId: string) => {
      fundraiserStore.setSelectedFundraiser(fundraiserId);
      navigate(`/fundraiser/${fundraiserId}`);
    };

    const handleInviteClick = (fundraiserId: string) => {
      fundraiserStore.setSelectedFundraiser(fundraiserId);
      navigate(`/fundraiser/promoter-invite`);
    };

    const handlePreviewClickFromTable = (fundraiserId: string) => {
      fundraiserStore.setSelectedFundraiser(fundraiserId);
      navigate(`/fundraiser/${fundraiserId}`);
    };

    const handleDeleteClick = (fundraiser: CreateFundraiserModel) => {
      setFundraiserToDelete(fundraiser);
      setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
      if (!canManageFundraiser("Delete")) return;
      if (fundraiserToDelete) {
        const response = await firstValueFrom(
          FundraiserService.deleteFundraiser(
            AuthService.authData.accountId,
            fundraiserToDelete.id,
            AuthService.mindmeAccountDetails?.userId || ""
          )
        );
        if (response.result.success) {
          ToastService.showSuccess("Fundraiser deleted successfully");
          fundraiserStore.setSelectedFundraiser(null);
          // fundraiserStore.setView("dashboard");
          navigate("/fundraiser/dashboard");
          setShowDeleteModal(false);
          setFundraiserToDelete(null);
          loadStats();
          fetchFundraisers();
        } else {
          ToastService.showError("Failed to delete fundraiser");
        }
      }
    };

    const handleLinkUpdatedAndCopied = (message: string) => {
      if (!urlError) {
        // urlError is from the old inline modal, remove this check
        ToastService.showSuccess("Share link updated and copied to clipboard");
      }
    };

    const resetFilters = () => {
      setFilters({
        status: [],
        amountMin: "",
        amountMax: "",
        dateMin: "",
        dateMax: "",
      });
    };

    const handleAIComplete = (fundraiserData: CreateFundraiserModel) => {
      setShowAIChat(false);
      // Create new fundraiser from AI-generated data
      fundraiserStore.addFundraiserFromAI(fundraiserData);
      navigate(`/fundraiser/${fundraiserData.id}`);
    };

    const handleManualCreate = () => {
      setShowAIChat(false);
      // Create blank fundraiser for manual creation
      fundraiserStore.createBlankFundraiser();
      navigate("/fundraiser/builder");
    };

    const handleFiltersChange = (newFilters: typeof filters) => {
      setFilters(newFilters);
    };

    const handleCreateFundraiser = () => {
      if (ApplicationSettingsStore.getOpenAISettings()) {
        setShowAIChat(true);
        return;
      } else {
        fundraiserStore.resetBuilder(AuthService.shortUniqueId);
        navigate("/fundraiser/builder");
      }
    };

    if (fundraiserStore.isLoadingFundRaisersGridData || isLoadingDashboard) {
      return (
        <DashboardContainer>
          <DashboardContent>
            {new Loader(true).loaderComponent}
          </DashboardContent>
        </DashboardContainer>
      );
    }

    if (showEmptyStatePlaceholder) {
      return (
        <FundraiserEmptyState
          onCreateFundraiser={() => {
            fundraiserStore.resetBuilder(AuthService.shortUniqueId);
            navigate("/fundraiser/builder");
          }}
          showToggleLink={fundraisers.length > 0}
          onToggleView={() => setShowEmptyStatePlaceholder(false)}
          merchantAccounts={fundraiserStore.merchantAccounts}
          onAICreate={() => onAICreate()}
        />
      );
    }

    return (
      <DashboardContainer>
        <DashboardContent>
          <Header>
            <HeaderLeft>
              <Title>Fundraisers</Title>
              <IconsContainer>
                {/* <IconButton
                  onClick={() => setShowEmptyStatePlaceholder(true)}
                  title="View Marketing Page"
                >
                  <Rocket size={20} />
                </IconButton> */}
                {/* <IconButton
                  onClick={() => navigate("/fundraiser/settings")}
                  title="Open Settings"
                >
                  <Settings size={20} />
                </IconButton> */}
                {/* <IconButton
                  onClick={() => setShowDonationCapture(true)}
                  title="Test Donation Form"
                >
                  <CreditCard size={20} />
                </IconButton> */}
                {/* <IconButton
                  onClick={() => {
                    // Hardcoded to use "Clean Water for All" fundraiser (ID: "1")
                    fundraiserStore.setSelectedFundraiser("1");
                  }}
                  title="Promoter Invite Page"
                >
                  <UserPlus size={20} />
                </IconButton> */}
              </IconsContainer>
            </HeaderLeft>
            <HeaderActions>
              <GalleryButton onClick={() => navigate("/fundraiser/gallery")}>
                <LayoutGrid size={16} />
                Gallery
              </GalleryButton>
              <CreateButton
                // disabled={!fundraiserStore.hasActiveMerchantAccount()}
                onClick={() => handleCreateFundraiser()}
              >
                <Plus size={20} />
                New Fundraiser
              </CreateButton>
            </HeaderActions>
          </Header>

          <DashboardStats stats={dashboardStats} />

          <TopPerformingFundraisers
            data={dashboardStats.topFundRaisers}
            onViewDetails={(fundraiserId) => {
              fundraiserStore.setSelectedFundraiser(fundraiserId);
              fundraiserStore.setSelectedTab("details");
              navigate("/fundraiser/fundraiser-details");
            }}
            onEdit={(fundraiserId) => {
              fundraiserStore.setSelectedFundraiser(fundraiserId);
              navigate("/fundraiser/builder");
            }}
            onPreview={(fundraiserId) => {
              fundraiserStore.setSelectedFundraiser(fundraiserId);
              navigate(`/fundraiser/${fundraiserId}`);
            }}
          />

          <Section>
            <SectionHeader>
              <SectionTitle>
                Fundraisers ({fundraiserStore.fundraisers.length})
              </SectionTitle>
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <SearchWrapper>
                  <SearchInput
                    type="text"
                    placeholder="Search fundraisers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <ClearButton
                      onClick={() => setSearchTerm("")}
                      title="Clear search"
                    >
                      <X size={16} />
                    </ClearButton>
                  )}
                </SearchWrapper>

                <FilterControls>
                  <FilterButton
                    active={showFilters}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter size={16} />
                    Filters
                    {(filters.status.length > 0 ||
                      filters.amountMin ||
                      filters.amountMax ||
                      filters.dateMin ||
                      filters.dateMax) && (
                      <FilterBadge>
                        {
                          [
                            filters.status.length > 0 ? 1 : 0,
                            filters.amountMin || filters.amountMax ? 1 : 0,
                            filters.dateMin || filters.dateMax ? 1 : 0,
                          ].filter(Boolean).length
                        }
                      </FilterBadge>
                    )}
                  </FilterButton>
                  <ResetButton
                    visible={hasActiveFilters}
                    onClick={resetFilters}
                  >
                    <RotateCcw size={12} />
                    Reset
                  </ResetButton>
                </FilterControls>
              </div>
            </SectionHeader>

            <SearchAndFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              hasActiveFilters={hasActiveFilters}
              onResetFilters={resetFilters}
            />

            <FundraisersTable
              fundraisers={fundraiserStore.fundraisers}
              searchTerm={searchTerm}
              hasActiveFilters={hasActiveFilters}
              onRowClick={(fundraiserId) => {
                fundraiserStore.setSelectedFundraiser(fundraiserId);
                fundraiserStore.setSelectedTab("details");
                navigate("/fundraiser/fundraiser-details");
              }}
              onViewDetails={(fundraiserId) => {
                fundraiserStore.setSelectedFundraiser(fundraiserId);
                fundraiserStore.setSelectedTab("details");
                navigate(`/fundraiser/fundraiser-details`);
              }}
              onEdit={(fundraiserId) => {
                fundraiserStore.setSelectedFundraiser(fundraiserId);
                navigate("/fundraiser/builder");
              }}
              onDonate={handlePreviewClickFromTable}
              onInvite={handleInviteClick}
              onLink={(fundraiserId) => handleLinkClick(fundraiserId)}
              onShare={(fundraiserId) => handleShareClick(fundraiserId)}
              onEmbed={handleEmbedClick}
              onCopy={(copyFundraiser) =>
                handleCopyClick(null as any, copyFundraiser)
              }
              onDeleteClick={handleDeleteClick}
            />
          </Section>

          <CopyFundraiserModal
            fundraiserId={selectedCopyCampaign?.id || ""}
            name={selectedCopyCampaign ? selectedCopyCampaign.name : ""}
            isVisible={showCopyModal}
            onClose={(refresh?: boolean) => {
              setShowCopyModal(false);
              setSelectedCopyCampaign(null);
              if (refresh) {
                fetchFundraisers();
              }
            }}
          />

          <SocialShareModal
            isVisible={showSocialShareModal}
            onClose={() => {
              setShowSocialShareModal(false);
              setSelectedModalFundraiser(null);
            }}
            gallerySettings={new GallerySettingsModel()}
            shortUniqueId={AuthService.shortUniqueId}
          />

          {selectedModalFundraiser && (
            <EmbedFundraiserModal
              isVisible={showEmbedModal}
              onClose={() => {
                setShowEmbedModal(false);
                setSelectedModalFundraiser(null);
              }}
              // fundraiser={selectedModalFundraiser}
            />
          )}

          {selectedModalFundraiser && (
            <UpdateLinkModal
              isVisible={showLinkModal}
              onClose={() => {
                setShowLinkModal(false);
                setSelectedModalFundraiser(null);
              }}
              fundraiser={selectedModalFundraiser}
              hasPromoters={
                fundraiserStore.getFundraiserPromoters(
                  selectedModalFundraiser.id
                ).length > 0
              }
              onLinkCopied={() =>
                copyFundraiserLink(selectedModalFundraiser.id)
              }
              onLinkUpdatedAndCopied={handleLinkUpdatedAndCopied}
            />
          )}

          {/* <DonationCapture
            isVisible={showDonationCapture}
            onClose={() => setShowDonationCapture(false)}
          /> */}

          <DeleteFundraiserModal
            isVisible={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setFundraiserToDelete(null);
            }}
            fundraiser={fundraiserToDelete}
            onConfirm={() => handleDeleteConfirm()}
          />

          <AIFundraiserChat
            isVisible={showAIChat}
            onClose={() => setShowAIChat(false)}
            onComplete={handleAIComplete}
            onManualCreate={handleManualCreate}
            merchantAccounts={fundraiserStore.merchantAccounts}
            onAICreate={() => onAICreate()}
          />
        </DashboardContent>
      </DashboardContainer>
    );
  }
);

export default withRouter(FundraiserDashboard);
